import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, BookOpen } from "lucide-react";
import { IMG } from "../lib/images";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/motion";

const BELIEFS = [
  { title: "Un Dieu unique", text: "Nous croyons en un seul Dieu, créateur de toutes choses, qui se révèle comme Père, Fils et Saint-Esprit.", refs: ["Deutéronome 6:4", "Matthieu 28:19", "2 Corinthiens 13:13"] },
  { title: "Jésus-Christ, Fils de Dieu", text: "Jésus-Christ est pleinement Dieu et pleinement homme. Il a vécu sans péché et il est le Sauveur du monde.", refs: ["Jean 1:1-14", "Colossiens 1:15-20", "Hébreux 1:1-3"] },
  { title: "La mort et la résurrection de Jésus", text: "Jésus est mort sur la croix pour nos péchés et il est ressuscité corporellement le troisième jour, victorieux de la mort.", refs: ["1 Corinthiens 15:3-4", "Romains 4:25", "Luc 24:1-7"] },
  { title: "Le salut par la grâce, au moyen de la foi", text: "Nous ne sommes pas sauvés par nos mérites, mais par la grâce de Dieu, reçue par la foi en Jésus-Christ.", refs: ["Éphésiens 2:8-9", "Romains 3:23-24", "Jean 3:16"] },
  { title: "La repentance et la nouvelle naissance", text: "Se tourner vers Dieu implique un changement de cœur : la repentance, et une vie nouvelle donnée par le Saint-Esprit.", refs: ["Jean 3:3-7", "Actes 2:38", "2 Corinthiens 5:17"] },
  { title: "L'autorité des Saintes Écritures", text: "La Bible est la Parole de Dieu, inspirée, digne de confiance, et elle fait autorité pour notre foi et notre vie.", refs: ["2 Timothée 3:16", "Psaume 119:105", "Hébreux 4:12"] },
  { title: "L'œuvre du Saint-Esprit", text: "Le Saint-Esprit convainc, console, transforme, et donne à chaque croyant des dons pour servir.", refs: ["Jean 14:16-17", "Actes 1:8", "Galates 5:22-23"] },
  { title: "La prière", text: "Dieu nous invite à lui parler simplement, à tout moment. La prière est le souffle de la vie chrétienne.", refs: ["Philippiens 4:6", "Matthieu 6:9-13", "1 Thessaloniciens 5:17"] },
  { title: "L'Église", text: "L'Église est la famille de Dieu : une communauté appelée à s'aimer, grandir ensemble et témoigner de Jésus.", refs: ["Actes 2:42-47", "Éphésiens 4:11-16", "1 Pierre 2:9-10"] },
  { title: "Le baptême", text: "Le baptême par immersion est un acte d'obéissance et de témoignage public de la foi en Jésus-Christ.", refs: ["Matthieu 28:19", "Actes 8:36-38", "Romains 6:3-4"] },
  { title: "La Sainte Cène", text: "Le pain et la coupe nous rappellent le corps et le sang de Jésus, donnés pour nous. Nous la partageons ensemble, dans la gratitude.", refs: ["Luc 22:19-20", "1 Corinthiens 11:23-26"] },
  { title: "Le retour de Jésus-Christ", text: "Jésus reviendra. Cette espérance nous encourage à vivre dans la fidélité, la joie et la vigilance.", refs: ["Jean 14:1-3", "Actes 1:11", "Apocalypse 22:12"] },
  { title: "La résurrection et l'espérance éternelle", text: "Nous croyons à la résurrection des morts et à la vie éternelle auprès de Dieu pour ceux qui ont mis leur foi en Jésus.", refs: ["1 Corinthiens 15:20-22", "Jean 11:25-26", "Apocalypse 21:3-4"] },
];

export default function Beliefs() {
  const [open, setOpen] = useState(0);
  return (
    <>
      <PageHeader
        testid="beliefs-header"
        eyebrow="Ce que nous croyons"
        titleLines={["Des convictions simples,", "enracinées dans la Bible"]}
        intro="Les grandes convictions chrétiennes qui fondent notre foi, présentées sobrement et accessibles à tous — avec leurs références bibliques."
        image={IMG.bibleStudy}
      />
      <section className="py-16 sm:py-24">
        <div className="container-site mx-auto max-w-3xl space-y-3">
          {BELIEFS.map((b, i) => {
            const isOpen = open === i;
            return (
              <Reveal key={b.title} delay={Math.min(i * 0.03, 0.3)}>
                <div data-testid={`belief-${i}`} className={`card-warm overflow-hidden transition-colors ${isOpen ? "border-terracotta/40" : ""}`}>
                  <button
                    data-testid={`belief-toggle-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 p-5 text-left sm:p-6"
                  >
                    <span className="flex items-center gap-4">
                      <span className="font-serif text-xl font-medium text-terracotta/40">{String(i + 1).padStart(2, "0")}</span>
                      <span className="font-serif text-base font-semibold tracking-tight text-obsidian sm:text-lg">{b.title}</span>
                    </span>
                    <motion.span animate={{ rotate: isOpen ? 180 : 0 }} className="shrink-0 text-terracotta">
                      <ChevronDown className="h-5 w-5" />
                    </motion.span>
                  </button>
                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                      >
                        <div className="px-5 pb-6 sm:px-6">
                          <p className="text-sm leading-relaxed text-stone-600 sm:text-base">{b.text}</p>
                          <div className="mt-4 flex flex-wrap items-center gap-2">
                            <BookOpen className="h-4 w-4 text-terracotta" />
                            {b.refs.map((r) => (
                              <span key={r} className="rounded-full bg-terracotta-soft px-3 py-1 text-xs font-semibold text-terracotta-deep">{r}</span>
                            ))}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </Reveal>
            );
          })}
          <Reveal className="pt-8 text-center">
            <p className="text-sm text-stone-500">
              Une question sur la foi ? <a href="/contact" className="font-semibold text-terracotta hover:underline">Écrivez-nous</a> — nous répondrons avec plaisir.
            </p>
          </Reveal>
        </div>
      </section>
    </>
  );
}
